import React, { useMemo } from 'react';
import { fetchColumnsData } from '../../dispatchers/ColumnSelectorDispatcher';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import RGL, { WidthProvider, Layout } from 'react-grid-layout';
import Loader from '../common/Loader/Loader';
import InstallationTableWidget from './InstallationTableWidget';
import {
  fetchGetComponentsData,
  fetchGetFailureCauseDetailsData,
  fetchGetFailureCausesData,
  fetchGetObservationsData,
  fetchGetPullReasonsData,
  fetchGetWells,
  fetchInstallationHistoryData,
} from '../../services/installationHistory/InstallationHistoryService';
import { ToastServiceProvider } from '../../components/toast/ToastService';
import {
  fetchLiftTypesEnum,
  fetchManufacturerControllers,
  fetchManufacturers,
} from '../../services/well-completions/WellCompletionsService';
import { ReactComponent as WarningIcon } from '../../images/alert-triangle.svg';

const GridLayout = WidthProvider(RGL);

const Installation = () => {
  const installationPageData = useAppSelector((state: any) => state.testWellColumnsSlice?.data?.widgets || []);
  const installationData = useAppSelector((state: any) => {
    const data = state.installationHistorySlice?.installationHistory?.data;
    return Array.isArray(data) ? data : [];
  });
  const [isWidgetLoading, setIsWidgetLoading] = React.useState(false);
  const isInstallationLoading = useAppSelector(
    (state: any) => state.installationHistorySlice?.installationHistory?.loading || false,
  );
  const wellId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);
  const wellName = useAppSelector((state: any) => state.WellTestIdSlice?.data?.Name);
  const selectedAssetId = useAppSelector((state) => state.assetGroups?.selectedAssetId);

  const dispatch = useAppDispatch();
  React.useEffect(() => {
    const fetchData = async () => {
      setIsWidgetLoading(true);
      await dispatch(fetchColumnsData(`installs`));
      setIsWidgetLoading(false);
    };

    fetchData();
  }, [dispatch]);

  React.useEffect(() => {
    if (wellId) {
      dispatch(fetchInstallationHistoryData(wellId));
      dispatch(fetchGetWells());
      dispatch(fetchLiftTypesEnum());
      dispatch(fetchManufacturers());
      dispatch(fetchManufacturerControllers());
      dispatch(fetchGetPullReasonsData());
      dispatch(fetchGetComponentsData());
      dispatch(fetchGetFailureCausesData());
      dispatch(fetchGetFailureCauseDetailsData());
      dispatch(fetchGetObservationsData());
    }
  }, [wellId]);

  const safeLayout = (installationPageData: any[]): Layout[] => {
    if (!Array.isArray(installationPageData)) {
      return [];
    }

    return installationPageData
      .map((obj: any, index: number): Layout | null => {
        const lg = obj?.layout?.lg;
        if (!lg || !obj?.key) {
          console.warn(`Invalid layout at index ${index}`, obj);
          return null;
        }

        const item: Layout = {
          i: String(obj.key),
          x: lg.x,
          y: lg.y,
          w: lg.width,
          h: lg.height,
          minH: lg.minH ?? 1,
          minW: lg.minW ?? 6,
        };

        return item;
      })
      .filter((item): item is Layout => item !== null);
  };

  const layoutMemo = useMemo(() => safeLayout(installationPageData), [installationPageData]);

  return (
    <ToastServiceProvider>
      <div className='installation-component-container'>
        {!wellId && (
          <div className='installation-warnings'>
            <div className='warnings-header'>
              <WarningIcon className='warning-icon' />
              <div className='warnings-header-text-and-list'>
                <div className='warnings-header-row'>
                  <span className='warnings-header-text'>Just to let you know this might be a problem</span>
                </div>
                <div className='warnings-content'>
                  <div className='warning-item'>
                    This well is not onboarded into pump checker correctly - {selectedAssetId ?? ''}. Contact your
                    administrator to resolve this.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {isInstallationLoading || isWidgetLoading ? (
          <div className='installation-loader-center'>
            <Loader />
          </div>
        ) : (
          <div>
            <GridLayout
              layout={layoutMemo}
              cols={12}
              rowHeight={30}
              isDraggable={false}
              isResizable={true}
              className='installation-layout-react-grid'
              useCSSTransforms={false}
              isDroppable={false}
            >
              {layoutMemo?.map((item: any) => (
                <div key={item.i} id={`grid-item-${item.i}`} className='grid-item'>
                  {item.i === 'install-table' && (
                    <InstallationTableWidget
                      widgetHeader={`${wellName ? `${wellName} - ` : ''}Installation history`}
                      tableData={installationData}
                    />
                  )}
                </div>
              ))}
            </GridLayout>
          </div>
        )}
      </div>
    </ToastServiceProvider>
  );
};

export default Installation;
